import { useContext } from 'react';

import type { ContextValue } from '../../components/SunriseHoldoutExperimentContextProvider';
import { SunriseHoldoutExperimentContext } from '../../components/SunriseHoldoutExperimentContextProvider';

export type EnabledState = {
	enabled: boolean;
};

export type HighlightTextProps = {
	selected: boolean;
};

export type HighlightTextState = {
	useAccentStyles: boolean;
};

export type IsSunriseEnabledProps = {
	isSunriseEnabled: boolean;
};

export type IsSunriseEnabledState = IsSunriseEnabledProps & EnabledState;

export type MembershipFormState = IsSunriseEnabledProps & {
	noSelected: boolean;
	yesSelected: boolean;
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	setUserInteracted: ContextValue['setUserInteracted'];
	noSelectionMadeError: ContextValue['noSelectionMadeError'];
	onSubmit: MembershipFormProps['onSubmit'];
};

export type MembershipFormProps = IsSunriseEnabledProps & {
	onSelectNo: () => void;
	onSelectYes: () => void;
	acceptMembership: boolean;
	onSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
};

export type SelectionProps = IsSunriseEnabledProps & {
	checked: boolean;
};

export type SelectionState = IsSunriseEnabledProps & {
	shouldHideRadioButtons: boolean;
	shouldShowSelectionBorder: boolean;
};

type Props = HighlightTextProps | IsSunriseEnabledProps | MembershipFormProps | SelectionProps;

export function useSunriseHoldoutExperiment(): EnabledState;
export function useSunriseHoldoutExperiment(props: HighlightTextProps): HighlightTextState;
export function useSunriseHoldoutExperiment(props: IsSunriseEnabledProps): IsSunriseEnabledState;
export function useSunriseHoldoutExperiment(props: MembershipFormProps): MembershipFormState;
export function useSunriseHoldoutExperiment(props: SelectionProps): SelectionState;
export function useSunriseHoldoutExperiment(
	props?: Props,
): EnabledState | HighlightTextState | IsSunriseEnabledState | MembershipFormState | SelectionState {
	const { setUserInteracted, userInteracted, variant, noSelectionMadeError, setNoSelectionMadeError } = useContext(
		SunriseHoldoutExperimentContext,
	);
	const enabled = isEnabledVariant(variant);

	if (!props) return { enabled };

	if (isHighlightTextState(props)) return highlightTextState(props.selected, variant);

	const isSunriseEnabled = enabled || props.isSunriseEnabled;

	if (isSelectionState(props)) return selectionState(isSunriseEnabled, props.checked, variant);

	const isMembershipFormState = 'acceptMembership' in props;
	if (!isMembershipFormState) {
		const isSunriseEnabledStateReturnValue: IsSunriseEnabledState = {
			enabled,
			isSunriseEnabled,
		};

		return isSunriseEnabledStateReturnValue;
	}

	const needsUserInteraction = variant === 'variant_2' && !userInteracted;

	const yesSelected = needsUserInteraction ? false : props.acceptMembership;
	const noSelected = needsUserInteraction ? false : !props.acceptMembership;

	function handleSelection(callback: MembershipFormProps['onSelectNo'] | MembershipFormProps['onSelectYes']) {
		return () => {
			setUserInteracted(true);
			setNoSelectionMadeError(false);
			callback();
		};
	}

	const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
		if (!yesSelected && !noSelected && variant === 'variant_2') {
			e.preventDefault();
			setNoSelectionMadeError(true);

			return;
		}

		await props.onSubmit(e);
	};

	const membershipFormStateReturnValue: MembershipFormState = {
		isSunriseEnabled,
		yesSelected,
		noSelected,
		onSelectNo: handleSelection(props.onSelectNo),
		onSelectYes: handleSelection(props.onSelectYes),
		acceptMembership: yesSelected,
		setUserInteracted,
		noSelectionMadeError: enabled && noSelectionMadeError,
		onSubmit: handleSubmit,
	};

	return membershipFormStateReturnValue;
}

function isHighlightTextState(props: Props): props is HighlightTextProps {
	return 'selected' in props;
}

function highlightTextState(selected: boolean, variant: string | undefined): HighlightTextState {
	return { useAccentStyles: selected || isEnabledVariant(variant) };
}

function isSelectionState(props: Props): props is SelectionProps {
	return 'checked' in props;
}

function selectionState(isSunriseEnabled: boolean, checked: boolean, variant: string | undefined): SelectionState {
	return {
		isSunriseEnabled,
		shouldHideRadioButtons: variant !== 'variant_1',
		shouldShowSelectionBorder: variant === 'variant_2' && !checked,
	};
}

function isEnabledVariant(variant: string | undefined) {
	return !['control', undefined].includes(variant);
}
